/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useEffect } from 'react';
import { Building2, Clock, Info, Save, CheckCircle, Key } from 'lucide-react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';

import toastService from '../utils/toastService';
import ContextWarning from '../components/ContextWarning';
import FileUpload from '../components/FileUpload';
import { subAccountService } from '../services/subAccountService';

// Named constants
const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;
const MIN_EXPIRY_MINUTES = process.env.REACT_APP_MIN_EXPIRY_MINUTES ? parseInt(process.env.REACT_APP_MIN_EXPIRY_MINUTES, 10) : 15;
const MAX_EXPIRY_HOURS = 8760;
const MAX_EXPIRY_DAYS = 365;
const MAX_EXPIRY_MINUTES = 525600;
const SAVE_MESSAGE_DURATION_MS = 2500;
const MAX_FILE_UPLOAD_SIZE = 10 * 1024 * 1024;

// Expiry presets with time units
const EXPIRY_PRESETS = [
  { value: 1, unit: 'hours', label: '1 Hour' },
  { value: 6, unit: 'hours', label: '6 Hours' },
  { value: 12, unit: 'hours', label: '12 Hours' },
  { value: 1, unit: 'days', label: '1 Day' },
  { value: 3, unit: 'days', label: '3 Days' },
  { value: 7, unit: 'days', label: '1 Week' },
  { value: 30, unit: 'days', label: '1 Month' }
];

const TIME_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' }
];

// Convert to hours for display consistency
const convertToHours = (value, unit) => {
  switch (unit) {
    case 'minutes': return value / 60;
    case 'hours': return value;
    case 'days': return value * 24;
    default: return value;
  }
};

const getMaxExpiryForUnit = (unit) => {
  switch (unit) {
    case 'minutes': return MAX_EXPIRY_MINUTES;
    case 'days': return MAX_EXPIRY_DAYS;
    case 'hours': return MAX_EXPIRY_HOURS
    default: return MAX_EXPIRY_HOURS;
  }
};

// Format expiry label for display
const formatExpiryLabel = (value, unit) => {
  if (!value || !unit) { return 'Not set'; }

  if (unit === 'minutes') {
    if (value === 1) { return '1 Minute'; }
    return `${value} Minutes`;
  } else if (unit === 'hours') {
    if (value === 1) { return '1 Hour'; }
    if (value === 24) { return '1 Day'; }
    return `${value} Hours`;
  } else if (unit === 'days') {
    if (value === 1) {return '1 Day'};
    if (value === DAYS_IN_WEEK) {return '1 Week';}
    if (value === DAYS_IN_MONTH) {return '1 Month';}
    return `${value} Days`;
  } else{
    return `${value} ${unit}`;
  }
};

const Settings = () => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [orgName, setOrgName] = useState('');
  const [expiryValue, setExpiryValue] = useState(24);
  const [expiryUnit, setExpiryUnit] = useState('hours');
  const [customValue, setCustomValue] = useState('');
  const [customUnit, setCustomUnit] = useState('hours');
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [industryLogoUrls, setIndustryLogoUrls] = useState({
    automotive: "",
    aviation: "",
    insurance: "",
    fieldServices: "",
    general: ""
  });
  const [captureProvenanceDetails, setCaptureProvenanceDetails] = useState(false);
  const [otpVerification, setOtpVerification] = useState(false);
  const [sendOtpVia, setSendOtpVia] = useState('email');

  useEffect(() => {
    document.body.classList.add('settings-page-no-scroll');
    return () => document.body.classList.remove('settings-page-no-scroll');
  }, []);

  useEffect(() => {
    if (!user?.subAccountUID) {
      setLoading(false);
      return;
    }
    apiClient.get('/settings')
      .then(r => {
        const value = r.data.defaultExpiry || 24;
        const unit = r.data.expiryTimeUnit || 'hours';

        setOrgName(r.data.name || '');
        setCaptureProvenanceDetails(r.data.captureProvenanceDetails || false);
        setOtpVerification(r.data.otpVerification || false);
        setSendOtpVia(r.data.sendOtpVia || 'email');
        if (r.data.industryLogoUrls) {
          setIndustryLogoUrls(prev => ({ ...prev, ...r.data.industryLogoUrls }));
        }

        // Check if current setting matches a preset
        const matchingPreset = EXPIRY_PRESETS.find(p => p.value === value && p.unit === unit);

        if (matchingPreset) {
          setExpiryValue(value);
          setExpiryUnit(unit);
          setUseCustom(false);
        } else {
          setExpiryValue(value);
          setExpiryUnit(unit);
          setCustomValue(String(value));
          setCustomUnit(unit);
          setUseCustom(true);
        }
      })
      .catch((err) => toastService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const effectiveValue = useCustom ? (parseInt(customValue, 10) || 0) : expiryValue;
  const effectiveUnit = useCustom ? customUnit : expiryUnit;

  const handlePresetSelect = (preset) => {
    setExpiryValue(preset.value);
    setExpiryUnit(preset.unit);
    setUseCustom(false);
  };

  const handleLogoUpload = async (file) => {
    const res = await subAccountService.uploadLogo(file, user?.subAccountUID);
    return res.data;
  };

  const handleSave = async () => {
    if (!effectiveValue || effectiveValue < 1) {
      setError('Expiry must be at least 1');
      return;
    }

    // Validate based on unit
    if (effectiveUnit === 'minutes' && effectiveValue < MIN_EXPIRY_MINUTES) {
      setError('Expiry must be at least 15 minute');
      return;
    }
    if (effectiveUnit === 'hours' && effectiveValue < 1) {
      setError('Expiry must be at least 1 hour');
      return;
    }
    if (effectiveUnit === 'days' && effectiveValue < 1) {
      setError('Expiry must be at least 1 day');
      return;
    }

    const maxAllowed = getMaxExpiryForUnit(effectiveUnit);
    if (effectiveValue > maxAllowed) {
      setError(`Expiry cannot exceed ${maxAllowed} ${effectiveUnit}`);
      return;
    }

    setError('');
    setSaving(true);
    try {
      const payload = {
        defaultExpiry: effectiveValue,
        expiryTimeUnit: effectiveUnit,
        captureProvenanceDetails: captureProvenanceDetails,
        otpVerification: otpVerification,
        sendOtpVia: otpVerification ? sendOtpVia : null,
        industryLogoUrls: industryLogoUrls
      };

      if (orgName.trim()) { payload.name = orgName.trim(); }

      await apiClient.put('/settings', payload);
      toastService.success('Settings saved successfully');
      setSaved(true);
      setTimeout(() => setSaved(false), SAVE_MESSAGE_DURATION_MS);
    } catch (err) {
      toastService.error(err.response?.data?.message[0] || err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!user?.subAccountUID) {
    return (
      <div className="">

        <div className="mb-4">
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Configure defaults for your organisation</p>
        </div>

        <ContextWarning title="Select a sub-account to edit settings" message="To update a setting, select a sub-account from the switcher in the bottom left" />

        <div className="flex flex-col items-center justify-center py-32 space-y-3 text-center">
          <Building2 size={40} className="text-base-content/20" />
          <p className="text-base-content/60 font-black uppercase tracking-widest text-xs">Sub-account required</p>
          <p className="text-base-content/40 text-sm max-w-xs">
            Settings are per sub-account. Use the account switcher to select a specific sub-account first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="mb-4">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Configure defaults for your organisation</p>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 space-y-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">Loading settings...</p>
        </div>
      ) : (
        <div className="space-y-4 max-w-[720px]">

          {/* Provenance Capture */}
          <div className="card-premium rounded-xl glass-panel p-4 sm:p-5 border border-base-content/10">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="rounded-md w-9 h-9 bg-primary/10 flex items-center justify-center border border-primary/20">
                  <Info size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-base-content tracking-tight">Allow Provenance Capture</h2>
                  <p className="text-[12px] font-semibold text-base-content/40 tracking-wide mt-0.5">
                    Enable capture of metadata for assignment tracking.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCaptureProvenanceDetails(!captureProvenanceDetails)}
                className={`relative inline-flex h-[20px] w-10 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${captureProvenanceDetails ? 'bg-primary' : 'bg-base-300/80 hover:bg-base-300'} cursor-pointer`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${captureProvenanceDetails ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </div>
          </div>

          {/* OTP Verification */}
          <div className="card-premium rounded-xl glass-panel p-4 sm:p-5 border border-base-content/10">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-md w-9 h-9 bg-primary/10 flex items-center justify-center border border-primary/20">
                    <Key size={18} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-base-content tracking-tight">OTP Verification</h2>
                    <p className="text-[12px] font-semibold text-base-content/40 tracking-wide mt-0.5">
                      Require OTP verification for users to access and submit captures.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOtpVerification(!otpVerification)}
                  className={`relative inline-flex h-[20px] w-10 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${otpVerification ? 'bg-primary' : 'bg-base-300/80 hover:bg-base-300'} cursor-pointer`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${otpVerification ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>

              {otpVerification && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-3 border-t border-base-content/5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-base-content/70">Send OTP Via:</span>
                    <select
                      value={sendOtpVia}
                      onChange={e => setSendOtpVia(e.target.value)}
                      className="rounded-md pl-3 pr-8 py-1.5 bg-base-100 border border-base-content/10 text-xs font-black focus:ring-0 focus:border-primary outline-none"
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>
                  <p className="text-[10px] font-bold text-base-content/40 uppercase tracking-wide">
                    Select the default delivery channel for verification codes.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Link Expiry */}
          <div className="card-premium rounded-xl glass-panel p-4 sm:p-5 border border-base-content/10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <div className="rounded-md w-9 h-9 bg-primary/10 flex items-center justify-center border border-primary/20 shrink-0">
                  <Clock size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-base-content tracking-tight">Default Link Expiry</h2>
                  <p className="text-[12px] font-semibold text-base-content/40 tracking-wide mt-0.5">
                    Controls how long capture links remain active by default.
                  </p>
                </div>
              </div>

              <div className="rounded-md flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/10 self-start md:self-center">
                <div className="w-1.5 h-1.5 bg-primary rounded-md"></div>
                <p className="text-[10px] font-black uppercase tracking-widest text-base-content/60">
                  Current: <span className="text-primary">{formatExpiryLabel(effectiveValue, effectiveUnit)}</span>
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Preset Options */}
              <div className="rounded-xl p-1 bg-base-200/50 border border-base-content/5 flex flex-wrap gap-1">
                {EXPIRY_PRESETS.map(preset => (
                  <button
                    key={`${preset.value}-${preset.unit}`}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={`rounded-md flex-1 min-w-[80px] py-2.5 px-3 text-[10px] font-black uppercase tracking-wider transition-all duration-200 border border-transparent ${!useCustom && expiryValue === preset.value && expiryUnit === preset.unit
                      ? 'bg-primary text-primary-content shadow-sm border-primary'
                      : 'text-base-content/50 hover:bg-base-100 hover:text-primary hover:shadow-md hover:border-base-content/10'
                      }`}
                  >
                    {preset.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setUseCustom(true)}
                  className={`rounded-md flex-1 min-w-[80px] py-2.5 px-3 text-[10px] font-black uppercase tracking-wider transition-all duration-200 border border-transparent ${useCustom
                    ? 'bg-primary text-primary-content shadow-sm border-primary'
                    : 'text-base-content/50 hover:bg-base-100 hover:text-primary hover:shadow-md hover:border-base-content/10'
                    }`}
                >
                  Custom
                </button>
              </div>

              {/* Custom Input or Preset Info */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {useCustom ? (
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max={getMaxExpiryForUnit(customUnit)}
                        value={customValue}
                        onChange={e => setCustomValue(e.target.value)}
                        placeholder="Enter value"
                        className="rounded-md w-24 pl-3 pr-2 py-2.5 bg-base-100 border border-base-content/10 text-xs font-black focus:ring-0 focus:border-primary outline-none"
                      />
                      <select
                        value={customUnit}
                        onChange={e => setCustomUnit(e.target.value)}
                        className="rounded-md pl-3 pr-8 py-2.5 bg-base-100 border border-base-content/10 text-xs font-black focus:ring-0 focus:border-primary outline-none"
                      >
                        {TIME_UNITS.map(unit => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {customValue && parseInt(customValue, 10) > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/10 rounded-md">
                        <span className="text-[14px] font-black text-base-content/30 uppercase">≈</span>
                        <span className="text-[10px] text-primary font-black uppercase tracking-wider">
                          {formatExpiryLabel(parseInt(customValue, 10), customUnit)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-base-content/40">
                    <Info size={14} />
                    <p className="text-[10px] font-bold uppercase tracking-widest">Preset selected</p>
                  </div>
                )}

                <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-base-content/30 text-right">
                  Workflows can override this value
                </p>
              </div>
            </div>
          </div>

          <div className='grid md:grid-cols-2 grid-col-1 gap-2'>
            <FileUpload
              id="logo_url"
              label="Automotive"
              currentUrl={industryLogoUrls.automotive}
              onUploadComplete={(url) => setIndustryLogoUrls(f => ({ ...f, automotive: url }))}
              onClear={() => setIndustryLogoUrls(u => ({ ...u, automotive: '' }))}
              uploadFn={handleLogoUpload}
              maxSize={MAX_FILE_UPLOAD_SIZE}
              className='border border-dotted'
              imageClassName='h-32 w-32 object-fill'
            />
            <FileUpload
              id="logo_url"
              label="Aviation"
              currentUrl={industryLogoUrls.aviation}
              onUploadComplete={(url) => setIndustryLogoUrls(f => ({ ...f, aviation: url }))}
              onClear={() => setIndustryLogoUrls(u => ({ ...u, aviation: '' }))}
              uploadFn={handleLogoUpload}
              maxSize={MAX_FILE_UPLOAD_SIZE}
              className='border border-dotted'
              imageClassName='h-32 w-32 object-fill'
            />
            <FileUpload
              id="logo_url"
              label="Insurance"
              currentUrl={industryLogoUrls.insurance}
              onUploadComplete={(url) => setIndustryLogoUrls(f => ({ ...f, insurance: url }))}
              onClear={() => setIndustryLogoUrls(u => ({ ...u, insurance: '' }))}
              uploadFn={handleLogoUpload}
              maxSize={MAX_FILE_UPLOAD_SIZE}
              className='border border-dotted'
              imageClassName='h-32 w-32 object-fill'
            />
            <FileUpload
              id="logo_url"
              label="Field Services"
              currentUrl={industryLogoUrls.fieldServices}
              onUploadComplete={(url) => setIndustryLogoUrls(f => ({ ...f, fieldServices: url }))}
              onClear={() => setIndustryLogoUrls(u => ({ ...u, fieldServices: '' }))}
              uploadFn={handleLogoUpload}
              maxSize={MAX_FILE_UPLOAD_SIZE}
              className='border border-dotted'
              imageClassName='h-32 w-32 object-fill'
            />
            <FileUpload
              id="logo_url"
              label="General"
              currentUrl={industryLogoUrls.general}
              onUploadComplete={(url) => setIndustryLogoUrls(f => ({ ...f, general: url }))}
              onClear={() => setIndustryLogoUrls(u => ({ ...u, general: '' }))}
              uploadFn={handleLogoUpload}
              maxSize={MAX_FILE_UPLOAD_SIZE}
              className='border border-dotted'
              imageClassName='h-32 w-32 object-fill'
            />
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 text-error px-5 py-4 rounded-2xl text-xs font-black uppercase tracking-widest">
              {error}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              onClick={handleSave}
              disabled={saving || !effectiveValue || effectiveValue < 1 || !can.settings.manage()}
              className="btn-premium"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : saved ? (
                <div className="flex items-center gap-2">
                  <CheckCircle size={17} /> Saved!
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Save size={17} /> Save Settings
                </div>
              )}
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default Settings;